import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SwipeableMenu from '../SwipeableMenu';
import ImageLoader from '../ImageLoader';
import { GridBackground } from '../GridBackground';
import VideoPlayer from '../VideoPlayer';

const DetalleEjercicio = ({ datosEjercicios }) => {
  let { grupo } = useParams();
  const [openIndex, setOpenIndex] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const rutinaActual = useMemo(() => {
    const data = datosEjercicios[grupo] || { titulo: "RUTINA DESCONOCIDA", ejercicios: [], imagenes: [] };
    if (grupo === 'piernas' && data.subgrupos) {
      return {
        ...data,
        ejercicios: data.subgrupos.flatMap(sg => sg.ejercicios.map(e => e.nombre))
      };
    }
    return data;
  }, [datosEjercicios, grupo]);

  const menuItems = ['pecho', 'espalda', 'hombros', 'biceps', 'triceps', 'cuadriceps', 'femorales', 'gluteos', 'abdominales'];

  return (
    <>
      {/* Botón para abrir el menú de rutinas */}
      <button
        onClick={() => setIsDrawerOpen(prev => !prev)}
        className={`fixed top-8 right-8 z-[60] bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 ${isDrawerOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Abrir menú de rutinas"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
        <span className="font-semibold text-sm">RUTINAS</span>
      </button>

      {/* Menú deslizable */}
      <SwipeableMenu isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <h3 className="bebas-font text-3xl text-blue-600 tracking-widest mb-4">RUTINAS</h3>
        <ul className="flex flex-col space-y-8">
          {menuItems.map(item => (
            <li key={item}>
              <Link
                to={`/${item}`}
                onClick={() => setIsDrawerOpen(false)}
                className={`text-lg p-2 rounded-md transition-colors ${grupo === item ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </Link>
            </li>
          ))}
        </ul>
      </SwipeableMenu>

      <div className="relative text-white min-h-screen p-6 md:p-8 bg-gray-900">
        <GridBackground />
        <div className="relative z-10 max-w-7xl mx-auto">
          <Link to="/" className="bebas-font inline-flex items-center gap-2 text-xl text-blue-400 hover:text-blue-300 transition-colors tracking-widest mb-8">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            VOLVER AL INICIO
          </Link>
          
          <div className="text-center mb-12">
            <h1 className="anton-font text-5xl md:text-7xl uppercase leading-tight tracking-normal">
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {rutinaActual.titulo}
              </span>
            </h1>
          </div>

          <div className="mt-12">
            <h2 className="anton-font text-4xl sm:text-5xl md:text-6xl text-white mb-8 text-center uppercase tracking-normal">GALERÍA DE EJERCICIOS</h2>
            {grupo === 'piernas' && rutinaActual.subgrupos ? (
              rutinaActual.subgrupos.map(subgrupo => (
                <div key={subgrupo.nombre} className="mb-16">
                  <h3 className="anton-font text-3xl sm:text-4xl text-blue-400 mb-6 uppercase tracking-normal border-l-4 border-blue-500 pl-4">{subgrupo.nombre}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subgrupo.ejercicios.map((imagen, index) => {
                      const isOpen = openIndex === `${subgrupo.nombre}-${index}`;
                      return (
                        <div key={imagen.nombre} className="glassmorphism-hover">
                          <div 
                            onClick={() => handleToggle(`${subgrupo.nombre}-${index}`)} 
                            className="relative h-80 rounded-2xl overflow-hidden shadow-xl transform transition-all hover:scale-[1.02] cursor-pointer border border-gray-800/50 hover:border-gray-700/50 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] group"
                          >
                            <ImageLoader src={imagen.src} alt={imagen.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                              <h4 className="text-white text-xl font-bold tracking-wide mb-1">{imagen.nombre}</h4>
                              <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                                <span>Ver Ejecución</span>
                                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[1000px] mt-4' : 'max-h-0'}`}>
                            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-gray-800/50 p-6 rounded-2xl space-y-4">
                              <p className="text-gray-300 leading-relaxed">{imagen.descripcion}</p>
                              <VideoPlayer videoUrl={isOpen ? imagen.videoUrl : ''} title={imagen.nombre} />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {rutinaActual.imagenes.map((imagen, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div key={imagen.nombre} className="glassmorphism-hover">
                      <div 
                        onClick={() => handleToggle(index)} 
                        className="relative h-80 rounded-2xl overflow-hidden shadow-xl transform transition-all hover:scale-[1.02] cursor-pointer border border-gray-800/50 hover:border-gray-700/50 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] group"
                      >
                        <ImageLoader src={imagen.src} alt={imagen.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                          <h4 className="text-white text-xl font-bold tracking-wide mb-1">{imagen.nombre}</h4>
                          <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                            <span>Ver Ejecución</span>
                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[1000px] mt-4' : 'max-h-0'}`}>
                        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-gray-800/50 p-6 rounded-2xl space-y-4">
                          <p className="text-gray-300 leading-relaxed">{imagen.descripcion}</p>
                          <VideoPlayer videoUrl={isOpen ? imagen.videoUrl : ''} title={imagen.nombre} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetalleEjercicio;
